// Investment maths: growth, required contributions, tax, inflation, and the
// prepay-vs-invest comparison. All rates are annual percentages.

import { amortize, summarizeLoan } from "./finance";

// Display order is fixed so every chart colours an instrument the same way.
export const INSTRUMENTS = [
  {
    id: "fd",
    legacyKey: "Fixed_Deposit",
    name: "Fixed Deposit",
    short: "FD",
    contribution: "lump",
    tax: "income",
    series: 1,
    note: "One-time deposit, interest taxed at your slab rate",
  },
  {
    id: "rd",
    legacyKey: "Recurring_Deposit",
    name: "Recurring Deposit",
    short: "RD",
    contribution: "monthly",
    tax: "income",
    series: 2,
    note: "Monthly deposit, interest taxed at your slab rate",
  },
  {
    id: "mf",
    legacyKey: "Mutual_Fund",
    name: "Mutual Fund SIP",
    short: "MF",
    contribution: "monthly",
    tax: "gains",
    series: 3,
    note: "Equity SIP; returns vary and are not guaranteed",
  },
  {
    id: "gold",
    legacyKey: "Sovereign_Gold_Bond",
    name: "Gold",
    short: "Gold",
    contribution: "lump",
    tax: "gains",
    series: 4,
    note: "Gold fund or bond; price-linked, can fall",
  },
  {
    id: "sa",
    legacyKey: "Savings_Account",
    name: "Savings Account",
    short: "Savings",
    contribution: "lump",
    tax: "income",
    series: 5,
    note: "Highly liquid, lowest return",
  },
  {
    id: "re",
    legacyKey: "Real_Estate",
    name: "Real Estate",
    short: "Real estate",
    contribution: "lump",
    tax: "gains",
    series: 6,
    note: "Illiquid; ignores rent, upkeep and transaction costs",
  },
];

export const INSTRUMENT_BY_ID = Object.fromEntries(INSTRUMENTS.map((i) => [i.id, i]));

// Conservative long-run assumptions. Users can change them in Settings.
export const DEFAULT_RETURNS = { fd: 7, rd: 6.8, mf: 12, gold: 8, sa: 3, re: 7 };

export const DEFAULT_ALLOCATION = { fd: 20, rd: 20, mf: 30, gold: 15, sa: 0, re: 15 };

export const DEFAULT_TAX = { slab: 30, capitalGains: 12.5 };

const pct = (v) => (Number(v) || 0) / 100;

export const monthlyRate = (annualPct) => Math.pow(1 + pct(annualPct), 1 / 12) - 1;

export function fvLump(amount, annualPct, months) {
  return amount * Math.pow(1 + pct(annualPct), months / 12);
}

/** Future value of a monthly contribution made at the start of each month. */
export function fvMonthly(amount, annualPct, months) {
  const m = monthlyRate(annualPct);
  if (months <= 0) return 0;
  if (m === 0) return amount * months;
  return amount * ((Math.pow(1 + m, months) - 1) / m) * (1 + m);
}

export function lumpNeeded(target, annualPct, months) {
  return target / Math.pow(1 + pct(annualPct), months / 12);
}

export function monthlyNeeded(target, annualPct, months) {
  const unit = fvMonthly(1, annualPct, months);
  return unit > 0 ? target / unit : 0;
}

/** What a future nominal amount is worth in today's money. */
export function realValue(nominal, inflationPct, months) {
  return nominal / Math.pow(1 + pct(inflationPct), months / 12);
}

/** Fisher real rate of return. */
export function realRate(annualPct, inflationPct) {
  return ((1 + pct(annualPct)) / (1 + pct(inflationPct)) - 1) * 100;
}

/** Post-tax annual rate for interest taxed every year at the slab rate. */
export function postTaxRate(annualPct, tax, taxRates = DEFAULT_TAX) {
  if (tax === "income") return (Number(annualPct) || 0) * (1 - pct(taxRates.slab));
  return Number(annualPct) || 0;
}

/**
 * Grow a stream of monthly contributions (index = month from now, paid at the
 * start of the month) and apply tax. Returns post-tax value at the end.
 */
export function growStream(contribs, annualPct, tax = "none", taxRates = DEFAULT_TAX) {
  const m = monthlyRate(postTaxRate(annualPct, tax, taxRates));
  let value = 0;
  let invested = 0;
  for (const c of contribs) {
    value = (value + c) * (1 + m);
    invested += c;
  }
  if (tax === "gains") value -= Math.max(0, value - invested) * pct(taxRates.capitalGains);
  return { value, invested, gains: value - invested };
}

/* ---------------------------------------------------------------------------
   Interest recovery / goal plan (the original Loan Recovery Pro calculator).
   Split a target across instruments and work out the one-time or monthly
   amount each needs so the combined corpus reaches the target.
--------------------------------------------------------------------------- */
export function recoveryPlan({
  target,
  months,
  allocation,
  returns,
  inflation = 0,
  inflationProof = false,
}) {
  const n = Math.max(1, Math.round(months));
  const goal = inflationProof ? target * Math.pow(1 + pct(inflation), n / 12) : target;
  const totalAlloc = INSTRUMENTS.reduce((a, i) => a + Math.max(0, allocation[i.id] || 0), 0) || 1;
  const yearsCount = Math.ceil(n / 12);

  const items = INSTRUMENTS.map((inst) => {
    const share = Math.max(0, allocation[inst.id] || 0) / totalAlloc;
    const rate = Number(returns[inst.id]) || 0;
    const part = goal * share;
    const monthly = inst.contribution === "monthly";
    if (share <= 0) {
      return { inst, rate, share: 0, active: false, part: 0, amount: 0, invested: 0, value: 0, gains: 0, returnPct: 0, real: 0, yearly: [] };
    }
    const amount = monthly ? monthlyNeeded(part, rate, n) : lumpNeeded(part, rate, n);
    const yearly = [];
    for (let y = 1; y <= yearsCount; y++) {
      const m = Math.min(y * 12, n);
      const value = monthly ? fvMonthly(amount, rate, m) : fvLump(amount, rate, m);
      const invested = monthly ? amount * m : amount;
      yearly.push({ year: y, months: m, value, invested, gains: value - invested, real: realValue(value, inflation, m) });
    }
    const invested = monthly ? amount * n : amount;
    return {
      inst,
      rate,
      share,
      active: true,
      part,
      amount,
      invested,
      value: part,
      gains: part - invested,
      returnPct: invested > 0 ? ((part - invested) / invested) * 100 : 0,
      real: realValue(part, inflation, n),
      yearly,
    };
  });

  const active = items.filter((i) => i.active);
  const best = active.length > 1 ? active.reduce((a, b) => (b.returnPct > a.returnPct ? b : a)) : null;

  const corpusByYear = [];
  for (let y = 1; y <= yearsCount; y++) {
    const row = { year: y, months: Math.min(y * 12, n) };
    let total = 0;
    for (const it of items) {
      const v = it.yearly[y - 1]?.value || 0;
      row[it.inst.id] = v;
      total += v;
    }
    row.total = total;
    corpusByYear.push(row);
  }

  const sum = (f) => active.reduce((a, i) => a + f(i), 0);
  return {
    goal,
    months: n,
    items,
    best,
    oneTime: sum((i) => (i.inst.contribution === "lump" ? i.amount : 0)),
    monthly: sum((i) => (i.inst.contribution === "monthly" ? i.amount : 0)),
    invested: sum((i) => i.invested),
    gains: sum((i) => i.gains),
    real: realValue(goal, inflation, n),
    corpusByYear,
  };
}

/* ---------------------------------------------------------------------------
   Prepay vs invest. Both strategies spend exactly the same cash every month
   until the loan's current closing date:
     • Invest: keep paying the loan as planned, invest the surplus.
     • Prepay: put the surplus into the loan; once it closes, invest everything
       that used to go to the loan plus the surplus.
   Compare what each leaves you with at that date (loan fully repaid in both).
--------------------------------------------------------------------------- */
export function prepayScenario({ loan, nowKey, surplus = 0, lumpSum = 0 }) {
  const base = summarizeLoan(loan, nowKey);
  if (base.status === "closed") return null;

  const fromKey = Math.max(nowKey, base.plan.startKey);
  const prepaid = amortize(loan, {
    additional: { monthly: surplus, fromKey, lump: lumpSum, lumpKey: fromKey },
  });

  const horizon = base.plan.endKey - fromKey + 1;
  const outflow = (sched, key) => {
    const idx = key - sched.startKey;
    if (idx < 0 || idx >= sched.rows.length) return 0;
    const r = sched.rows[idx];
    return r.payment + r.extra;
  };

  const investStream = [];
  const prepayStream = [];
  for (let t = 0; t < horizon; t++) {
    const key = fromKey + t;
    const budget = outflow(base.plan, key) + surplus + (t === 0 ? lumpSum : 0);
    investStream.push(budget - outflow(base.plan, key));
    prepayStream.push(Math.max(0, budget - outflow(prepaid, key)));
  }

  return {
    base,
    prepaid,
    fromKey,
    horizon,
    interestSaved: Math.max(0, base.plan.totalInterest - prepaid.totalInterest),
    monthsSaved: Math.max(0, base.plan.months - prepaid.months),
    closeKey: prepaid.endKey,
    investStream,
    prepayStream,
  };
}

export function compareAtRate(scenario, annualPct, tax, taxRates, inflation = 0) {
  const invest = growStream(scenario.investStream, annualPct, tax, taxRates);
  const prepay = growStream(scenario.prepayStream, annualPct, tax, taxRates);
  const toReal = (v) => realValue(v, inflation, scenario.horizon);
  return {
    rate: annualPct,
    invest,
    prepay,
    investReal: toReal(invest.value),
    prepayReal: toReal(prepay.value),
    advantage: invest.value - prepay.value,
  };
}

/** Sweep returns to find where investing starts to beat prepaying. */
export function breakEvenSweep(scenario, tax, taxRates, { from = 0, to = 18, step = 0.5 } = {}) {
  const points = [];
  for (let r = from; r <= to + 1e-9; r += step) {
    const c = compareAtRate(scenario, r, tax, taxRates);
    points.push({ rate: Number(r.toFixed(2)), invest: c.invest.value, prepay: c.prepay.value });
  }
  let breakEven = null;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const da = a.invest - a.prepay;
    const db = b.invest - b.prepay;
    if (da < 0 && db >= 0) {
      breakEven = a.rate + ((b.rate - a.rate) * -da) / (db - da);
      break;
    }
  }
  if (breakEven == null && points.length && points[0].invest >= points[0].prepay) breakEven = 0;
  return { points, breakEven };
}

/** Year-by-year nominal vs real growth of a lump sum plus monthly contributions. */
export function inflationSeries({ initial = 0, monthly = 0, annualPct, inflation, years }) {
  const out = [];
  for (let y = 0; y <= years; y++) {
    const m = y * 12;
    const nominal = fvLump(initial, annualPct, m) + fvMonthly(monthly, annualPct, m);
    const invested = initial + monthly * m;
    const real = realValue(nominal, inflation, m);
    out.push({ year: y, nominal, real, invested, erosion: nominal - real });
  }
  return out;
}
